import { datadogRum } from '@datadog/browser-rum';
import { DatadogProvider } from '@datadog/openfeature-browser';
import { OpenFeature } from '@openfeature/react-sdk';

let isInitialized = false;

// Datadog RUM applicationId/clientToken are public identifiers, not secrets,
// and are safe to ship in client-side code.
const DATADOG_CONFIG = {
  applicationId: 'a8fec0b2-49e3-41d7-9a1f-20ef563073c8',
  clientToken: 'pub34511b35dd7f01b91b18894ee1c0acd0',
  site: 'us3.datadoghq.com',
  service: 'onemedical',
  env: process.env.GATSBY_DATADOG_ENV ?? process.env.NODE_ENV ?? 'development',
  version: '1.0.0',
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
};

const getOrCreateContextUser = (): string => {
  const sessionKey = 'onemedical_context_user_id';
  const existingUserId = sessionStorage.getItem(sessionKey);

  if (existingUserId) {
    return existingUserId;
  }

  const newUserId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  sessionStorage.setItem(sessionKey, newUserId);
  return newUserId;
};

// Initializes Datadog RUM + OpenFeature (Datadog AB testing) providers.
// Safe to call multiple times; only runs once per browser session.
export const initDatadog = () => {
  if (typeof window === 'undefined' || isInitialized) {
    return;
  }

  const { applicationId, clientToken, site, service, env, version } =
    DATADOG_CONFIG;

  isInitialized = true;

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service,
    env,
    version,
    sessionSampleRate: DATADOG_CONFIG.sessionSampleRate,
    sessionReplaySampleRate: DATADOG_CONFIG.sessionReplaySampleRate,
    defaultPrivacyLevel: 'mask-user-input',
    trackUserInteractions: true,
  });

  const provider = new DatadogProvider({
    applicationId,
    clientToken,
    env,
    site,
    service,
    version,
    enableExposureLogging: true,
    enableFlagEvaluationTracking: true,
    rum: {
      sdk: datadogRum,
      ddFlaggingTracking: true,
    },
  });

  const contextUser = getOrCreateContextUser();
  const evaluationContext = {
    targetingKey: contextUser,
    userId: contextUser,
    userRole: 'customer',
  };

  datadogRum.setUser({ id: contextUser });

  OpenFeature.setProvider(provider, evaluationContext);
};
